import React, { useState } from 'react';
import { Ticket, Loader2, CheckCircle2, Percent } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Inline promo-code redemption form for the account page.
 */
export default function RedeemPromoCode({ user, onSuccess }) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const now = new Date();
  const isInActiveTrial = !!(user?.trial_end_at && new Date(user.trial_end_at) > now);
  const hasActiveDiscount = !!(
    user?.billing_discount_percent
    && (
      !user?.billing_discount_until
      || new Date(user.billing_discount_until) > now
    )
  );
  const blocked = isInActiveTrial || hasActiveDiscount;

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setSubmitting(true);
    try {
      const r = await fetch('/api/account/redeem-promo-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('assetlens_token')}`,
        },
        body: JSON.stringify({ code: code.trim() }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      toast.success(data.message || 'Code redeemed.');
      setCode('');
      if (onSuccess) onSuccess(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const fmtDate = (s) => new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
      <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-1">
        <Ticket size={18} className="text-emerald-400" /> Redeem a Promo Code
      </h2>
      {isInActiveTrial ? (
        <div className="mt-3 flex items-center gap-2 text-emerald-300 text-sm">
          <CheckCircle2 size={16} />
          <span>
            Your trial runs until <strong>{fmtDate(user.trial_end_at)}</strong>.
            You can redeem a new code once the current trial ends.
          </span>
        </div>
      ) : hasActiveDiscount ? (
        <div className="mt-3 flex items-center gap-2 text-violet-300 text-sm">
          <Percent size={16} />
          <span>
            You have <strong>{user.billing_discount_percent}% off</strong>
            {user.billing_discount_until
              ? <> until <strong>{fmtDate(user.billing_discount_until)}</strong></>
              : <> for life</>}
            . It applies when you subscribe.
          </span>
        </div>
      ) : (
        <>
          <p className="text-slate-500 text-xs">
            Enter a trial or discount code. Each code can only be redeemed once per account.
          </p>
          <form onSubmit={submit} className="mt-3 flex flex-wrap gap-2">
            <input
              type="text"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="INVEST-XXXX-XXXX"
              autoComplete="off"
              spellCheck="false"
              disabled={blocked}
              className="flex-1 min-w-[200px] px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-emerald-300 font-mono uppercase placeholder-slate-600 focus:border-emerald-500 outline-none disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={submitting || !code.trim() || blocked}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white text-sm font-semibold rounded-lg"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Ticket size={14} />}
              Redeem
            </button>
          </form>
        </>
      )}
    </div>
  );
}
